import React from "react";
import { useTranslation } from "react-i18next";
import { DoubleMaterialityIroTypeEnum } from "@green0meter/api-client";

import { HEADER_VALUE } from "../AssignStakeholders.constants";

import { Box, Button, Checkbox, Typography } from "@components/base";
import Table, { TableColumn } from "@components/common/Table";
import IconAssignUsers from "@icons/AssignUsers";
import type { QuestionItemType } from "@api";

type StringSet = Set<string>;

type Row = { id: string; value: string; label: string; count: number };

interface IroSelectionProps {
  allQuestions: QuestionItemType[];
  iroOptions: DoubleMaterialityIroTypeEnum[];
  selectedIro: StringSet;
  onToggle: (value: string) => void;
  onToggleAll: () => void;
  isAllSelected: boolean;
  onAssign: (level: "iro", label: string) => void;
  sectionRef?: React.RefObject<HTMLDivElement>;
  selectedStakeholderType: string;
}

const IroSelection: React.FC<IroSelectionProps> = ({
  allQuestions,
  iroOptions,
  selectedIro,
  onToggle,
  onToggleAll,
  isAllSelected,
  onAssign,
  sectionRef,
  selectedStakeholderType,
}) => {
  const { t } = useTranslation();

  const iroLabelMap = {
    [DoubleMaterialityIroTypeEnum.Impact]: t(
      "doubleMateriality.iro.impact",
      "Impact",
    ),
    [DoubleMaterialityIroTypeEnum.Risk]: t("doubleMateriality.iro.risk", "Risks"),
    [DoubleMaterialityIroTypeEnum.Opportunity]: t(
      "doubleMateriality.iro.opportunity",
      "Opportunities",
    ),
  };

  const rows: Row[] = iroOptions
    .map((enumValue) => {
      const value = String(enumValue);
      const questionIroArray = (q: QuestionItemType): DoubleMaterialityIroTypeEnum[] =>
        Array.isArray(q.iro) ? q.iro : [q.iro];

      return {
        id: value,
        value,
        label: iroLabelMap[enumValue],
        count: allQuestions.filter((q) => questionIroArray(q).includes(enumValue))
          .length,
      };
    })
    .filter((row) => row.count > 0);

  const columns: TableColumn<Row>[] = [
    {
      title: t("common.select", "Select"),
      key: "select",
      renderCellContent: (row) => (
        <Checkbox
          checked={
            row.value === HEADER_VALUE ? isAllSelected : selectedIro.has(row.value)
          }
          onChange={() =>
            row.value === HEADER_VALUE ? onToggleAll() : onToggle(row.value)
          }
        />
      ),
      tableCellProps: { align: "center", sx: { width: 80 } },
    },
    {
      title: t("common.name", "Name"),
      key: "label",
      renderCellContent: (row) => (
        <Typography variant={row.value === HEADER_VALUE ? "subtitle2" : "body2"}>
          {row.label}
        </Typography>
      ),
    },
    {
      title: t("assignIro.button", "Assign IRO"),
      key: "assign",
      renderCellContent: (row) => (
        <Button
          variant="text"
          size="small"
          startIcon={<IconAssignUsers />}
          onClick={(e) => {
            e.stopPropagation();
            const label =
              row.value === HEADER_VALUE ? t("common.all", "All") : row.label;

            if (row.value === HEADER_VALUE) {
              if (!isAllSelected) {
                onToggleAll();
              }
            } else if (!selectedIro.has(row.value)) {
              onToggle(row.value);
            }

            onAssign("iro", label);
          }}
          disabled={!selectedStakeholderType}
        >
          {t("assignIro.button", "Assign IRO")}
        </Button>
      ),
      tableCellProps: { align: "right", sx: { width: 180 } },
    },
  ];

  return (
    <Box ref={sectionRef} sx={{ marginLeft: "64px !important" }}>
      <Table
        columns={columns}
        data={[
          {
            id: "",
            value: HEADER_VALUE,
            label: t("doubleMateriality.iro.title", "IRO"),
            count: 0,
          },
          ...rows,
        ]}
        tableProps={{ size: "small", sx: { "& thead": { display: "none" } } }}
        onRowClick={(row) =>
          row.value === HEADER_VALUE ? onToggleAll() : onToggle(row.value)
        }
      />
    </Box>
  );
};

export default IroSelection;
